/**
 * The double-spend guard.
 *
 * A speculative fetch and the parent's real pick must collapse onto ONE
 * promise, or every speculation is a second 4c image. The whole contract is:
 *
 *   - one entry per key, created on first call
 *   - NEVER evicted on success — evicting on success re-opens exactly the
 *     window the speculation existed to close (the prototype gets this right:
 *     prototype/static/index.html deletes only inside .catch)
 *   - evicted on failure, so a retry is possible
 *
 * Keys come from lib/keys.ts and only from lib/keys.ts. Two call sites that
 * derive a key differently have already double-spent.
 */

type Entry = { promise: Promise<unknown>; startedAt: number; settled: 'pending' | 'ok' | 'error' };

const inflight = new Map<string, Entry>();
const listeners = new Set<() => void>();

function notify() {
  for (const fn of [...listeners]) fn();
}

export function onMemoChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function once<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const hit = inflight.get(key);
  if (hit) return hit.promise as Promise<T>;

  const entry: Entry = { promise: null as never, startedAt: Date.now(), settled: 'pending' };
  // Promise.resolve().then(fn) rather than fn(): a synchronous throw inside fn
  // would otherwise escape before the entry is recorded, leaving the map and
  // the caller disagreeing about whether the work started.
  entry.promise = Promise.resolve()
    .then(fn)
    .then(
      (value) => {
        entry.settled = 'ok';
        notify();
        return value;
      },
      (err) => {
        entry.settled = 'error';
        inflight.delete(key);
        notify();
        throw err;
      },
    );
  inflight.set(key, entry);
  notify();
  return entry.promise as Promise<T>;
}

/** True if this key has already been paid for (or is being paid for now). */
export function isMemoized(key: string): boolean {
  return inflight.has(key);
}

export function memoSnapshot(): { key: string; settled: string; ageMs: number }[] {
  const now = Date.now();
  return [...inflight.entries()].map(([key, e]) => ({
    key,
    settled: e.settled,
    ageMs: now - e.startedAt,
  }));
}

/** Dev-panel affordance only. Never call this from product code. */
export function clearMemo(): void {
  inflight.clear();
  notify();
}
